/**
 * `/pr/people/:id` — a PR CONTACT's profile (2026-07-14; re-homed under `/pr`
 * 2026-07-19 when Peter took PR over from the deep worker).
 *
 * Reached from the `/pr` people card and the `/data/outreach_targets` browser.
 * Renders ONE contact (an `outreach_targets` row): a header (identity + contact
 * routes + status + live email engagement + suggested angle) plus the FULL
 * cross-channel thread + reply composer — rendered by the SHARED
 * `ConversationView` component (the exact proven one the member page uses), so
 * the contact profile inherits the battle-tested chat rendering instead of a
 * re-implementation. All from one aggregate fetch, `GET /api/outreach/:id`.
 *
 * Every header field is editable IN PLACE (2026-07-22): each row is the same
 * hover-Edit chrome the member page's Profile block uses, and each save
 * dispatches a one-field `update_outreach_target` as `human:staff` — the SAME
 * canonical action Peter calls, so the dashboard and the agent share one write
 * path onto `outreach_targets`. Adding a phone here is what lights up the
 * WhatsApp column: the thread query + the composer's WhatsApp target both key
 * on `outreach_targets.phone`, so after a save-and-refetch the WhatsApp reply
 * button arms and any past/future messages with that number appear in the
 * thread. Field catalog + draft rules live in `lib/pr-person-edit.ts`.
 *
 * The thread only fills up because the `gmail_inbound_scanner`'s persist gate
 * has an `outreach_targets` arm alongside its `members` arm — a journalist has
 * no member row, so a member-only gate left this card permanently empty for
 * exactly the people it is for.
 *
 * Peter (agent) reads/writes the same row via the `list/update_outreach_target`
 * canonical actions, and replies auto-flip the contact to `responded` (email via
 * the Gmail scanner; WhatsApp/Instagram via the outreach_reply_scanner cron).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, type OutreachDetailResponse } from "../lib/api";
import { studioFormat } from "../lib/studio-tz";
import { useDispatch } from "../lib/use-dispatch";
import { PR_EDITABLE_FIELDS } from "../lib/pr-person-edit";
import { InlineFieldEditor } from "../components/InlineFieldEditor";
import { PrEngagementsPanel } from "../components/PrEngagementsPanel";
import {
  ConversationView,
  deriveEmailReplySubject,
} from "../components/ConversationView";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/** Map an outreach status to a tag tone. */
function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (["responded", "contacted"].includes(s)) return "tag-success";
  if (["dropped", "dormant"].includes(s)) return "tag-error";
  return "";
}

// InlineFieldEditor extracted to `components/InlineFieldEditor.tsx` (operator-inbox
// slice 5) so `/inbox/threads/:id` edits the SAME row through the same
// one-field dispatch. Behaviour unchanged.

export function PrPersonDetail(): JSX.Element {
  const { id = "" } = useParams();
  const [data, setData] = useState<OutreachDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useMemo(
    () => async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        setData(await api.outreachDetail(id));
      } catch (e) {
        setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Per-contact "Instagram AI" switch (2026-07-24) — same chip as the member
  // page. Unconditional hook (before the early returns); the chip renders only
  // when the contact has an instagram_dm_threads row.
  const igModeDispatch = useDispatch<
    { readonly igsid: string; readonly mode: "notify" | "agent" },
    { readonly mode: string }
  >("set_instagram_thread_mode");

  if (loading) return <p style={{ padding: 16 }}>Loading…</p>;
  if (error !== null) {
    return (
      <div style={{ padding: 16 }}>
        <p style={{ color: "var(--c-danger-text-41)" }}>{error}</p>
        <Link to="/pr">← Back to PR</Link>
      </div>
    );
  }
  if (data === null) return <p style={{ padding: 16 }}>Not found.</p>;

  const c = data.contact;

  // Current stored value per editable field, editor-shaped ('' when unset).
  const editorValue = (field: string): string => {
    const v = (c as unknown as Record<string, unknown>)[field];
    if (v === null || v === undefined) return "";
    return String(v);
  };
  // Read-view flourishes the plain value lacks (@handle, thousands separator).
  const displayValue = (field: string): string | undefined => {
    if (field === "instagram" && c.instagram !== null) return `@${c.instagram}`;
    if (field === "audience_size" && c.audience_size !== null)
      return c.audience_size.toLocaleString();
    return undefined;
  };

  return (
    <div style={{ padding: 16, maxWidth: 860, margin: "0 auto" }}>
      <Link to="/pr" style={{ fontSize: "0.85rem" }}>
        ← Back to PR
      </Link>

      {/* Header */}
      <div style={{ margin: "8px 0 16px" }}>
        <h1 style={{ margin: "0 0 4px", fontSize: "1.4rem" }}>
          {c.name}{" "}
          <span className={`tag ${statusTone(c.status)}`}>{c.status}</span>{" "}
          <span className="tag">{c.type}</span>
        </h1>
        <p style={{ margin: "0 0 8px", color: "var(--c-ink-41)" }}>
          {[c.occupation, c.company_outlet, c.location]
            .filter((v): v is string => v !== null && v.length > 0)
            .join(" · ") || "—"}
        </p>

        {/* Editable profile grid — EVERY field renders (— when unset) so an
            absent phone/Instagram still offers its hover-Edit affordance. The
            two prose fields get a full-width row below the grid. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "4px 16px",
            fontSize: "0.85rem",
          }}
        >
          {PR_EDITABLE_FIELDS.filter((s) => s.kind !== "multiline").map((s) => (
            <InlineFieldEditor
              key={s.field}
              entityId={c.id}
              action="update_outreach_target"
              idField="outreach_target_id"
              spec={s}
              value={editorValue(s.field)}
              display={displayValue(s.field)}
              onSaved={() => void load()}
            />
          ))}
          {c.last_email_event !== null ? (
            <div>
              Email: {c.last_email_event.type} ·{" "}
              {fmtDateTime(c.last_email_event.at)}
            </div>
          ) : null}
          {c.contact_date !== null ? (
            <div>Contacted: {fmtDateTime(c.contact_date)}</div>
          ) : null}
          {c.response_date !== null ? (
            <div>Responded: {fmtDateTime(c.response_date)}</div>
          ) : null}
        </div>

        <div style={{ marginTop: 8, fontSize: "0.85rem" }}>
          {PR_EDITABLE_FIELDS.filter((s) => s.kind === "multiline").map((s) => (
            <InlineFieldEditor
              key={s.field}
              entityId={c.id}
              action="update_outreach_target"
              idField="outreach_target_id"
              spec={s}
              value={editorValue(s.field)}
              onSaved={() => void load()}
            />
          ))}
        </div>
      </div>

      {/* Collaborations — the engagement lifecycle, fetched by dispatching the
          READ action rather than a new GET route. Also closes the navigation
          gap where a /pr pipeline row landed on a page that never mentioned
          the collaboration you clicked. */}
      <PrEngagementsPanel targetId={c.id} targetType={c.type} />

      {/* Conversation — the SAME proven component the member page uses. No AI
          auto-responder for a contact, hence no handover slot. The Instagram AI
          chip (default Off) is the ONLY way this contact's DMs reach the
          agent. */}
      <ConversationView
        messages={data.messages}
        phone={data.recipient_phone}
        email={data.recipient_email}
        igsid={data.recipient_igsid}
        emailSubjectDefault={deriveEmailReplySubject(data.messages)}
        onSent={() => void load()}
        emailTracking={data.email_tracking}
        {...(data.instagram_thread !== null
          ? {
              instagramAgent: {
                mode: data.instagram_thread.mode,
                busy: igModeDispatch.loading,
                onToggle: (next: "notify" | "agent") => {
                  const thread = data.instagram_thread;
                  if (thread === null) return;
                  void igModeDispatch
                    .run({ igsid: thread.igsid, mode: next })
                    .then((r) => {
                      if (r.ok) void load();
                    });
                },
              },
            }
          : {})}
      />
    </div>
  );
}
